import { apiClient } from './client'
import type { MeasurementUnit } from '../measurements'
import type { HikeDifficulty, HikeDifficultyCalculation, HikeDifficultyMode, WireHikeDifficulty } from '../hikeDifficulty'
import type { PlanGenerationProfile } from '../planGenerationProfile'

export type ItineraryDay = {
  dayNumber: number
  distanceKm?: number | null
  elevationGainMeters?: number | null
  elevationLossMeters?: number | null
  sourceMeasurementUnit?: MeasurementUnit | null
}

export type TourSummary = {
  id: string
  operatorId: string
  name: string
  link?: string | null
  description?: string | null
  country?: string | null
  stateOrProvince?: string | null
  geographicRegion?: string | null
  city?: string | null
  protectedArea?: string | null
  difficulty?: string | null
  difficultyLevel?: WireHikeDifficulty | null
  difficultyMode?: HikeDifficultyMode | null
  difficultyCalculation?: HikeDifficultyCalculation | null
  planGenerationProfile?: PlanGenerationProfile | null
  packWeight?: string | null
  highestPoint?: string | null
  aboveEightThousandFeet?: boolean | null
  itinerary: ItineraryDay[]
  totalDistanceKm?: number | null
  totalElevationGainMeters?: number | null
  totalElevationLossMeters?: number | null
  sourceMeasurementUnit?: MeasurementUnit | null
  heroImageUrls: string[]
}

export type TourOperator = {
  id: string
  shortId?: string | null
  name: string
  logoUrl?: string | null
  referralCode?: string | null
  tours: TourSummary[]
}

export type PlatformAdminTourOperator = TourOperator & {
  sortPriority: number
}

export type TourReadiness = {
  tourId: string
  operatorId: string
  operatorName: string
  tourName: string
  groupId?: string | null
  groupMemberCount: number
  travelerCount: number
  scoredTravelerCount: number
  averageHikeStrongIndex?: number | null
  tripReadinessScore?: number | null
}

export type OperatorUpsertRequest = {
  name: string
  shortId?: string | null
  referralCode?: string | null
  sortPriority?: number | null
}

export type TourUpsertRequest = {
  name: string
  link?: string | null
  description?: string | null
  country?: string | null
  stateOrProvince?: string | null
  geographicRegion?: string | null
  city?: string | null
  protectedArea?: string | null
  difficulty?: string | null
  difficultyLevel?: HikeDifficulty | null
  difficultyMode: HikeDifficultyMode
  planGenerationProfile: PlanGenerationProfile
  packWeight?: string | null
  highestPoint?: string | null
  aboveEightThousandFeet?: boolean | null
  sourceMeasurementUnit?: MeasurementUnit | null
  itinerary: ItineraryDay[]
}

export async function getTourOperators() {
  const { data } = await apiClient.get<TourOperator[]>('/operators')
  return data
}

export async function getPlatformAdminTourOperators() {
  const { data } = await apiClient.get<PlatformAdminTourOperator[]>('/api/admin/operators/management')
  return data
}

export async function getTourOperator(id: string) {
  const { data } = await apiClient.get<TourOperator>(`/operators/${id}`)
  return data
}

export async function createOperator(
  name: string,
  referralCode?: string | null,
  shortId?: string | null,
  sortPriority = 0
) {
  const payload: OperatorUpsertRequest = { name, shortId, referralCode, sortPriority }
  const { data } = await apiClient.post<PlatformAdminTourOperator>('/operators', payload)
  return data
}

export async function updateOperator(
  id: string,
  name: string,
  referralCode?: string | null,
  shortId?: string | null,
  sortPriority?: number
) {
  const payload: OperatorUpsertRequest = { name, shortId, referralCode, sortPriority }
  const { data } = await apiClient.put<PlatformAdminTourOperator>(`/operators/${id}`, payload)
  return data
}

export async function deleteOperator(id: string) {
  await apiClient.delete(`/operators/${id}`)
}

export type DeleteAllOperatorsResult = {
  deletedOperatorCount: number
  deletedTourCount: number
}

export async function deleteAllOperators() {
  const { data } = await apiClient.delete<DeleteAllOperatorsResult>('/api/admin/operators/all')
  return data
}

export async function importTourOperators(payload: TourOperator[]) {
  await apiClient.post(`/operators/import`, payload)
}

export async function createTour(operatorId: string, payload: TourUpsertRequest) {
  const { data } = await apiClient.post<TourSummary>(`/operators/${operatorId}/tours`, payload)
  return data
}

export async function updateTour(tourId: string, payload: TourUpsertRequest) {
  const { data } = await apiClient.put<TourSummary>(`/api/admin/tours/${tourId}`, payload)
  return data
}

export async function deleteTour(tourId: string) {
  await apiClient.delete(`/api/admin/tours/${tourId}`)
}

export type DeleteAllToursResult = {
  operatorCount: number
  deletedTourCount: number
}

export async function deleteAllTours(operatorId?: string | null) {
  const { data } = await apiClient.delete<DeleteAllToursResult>('/api/admin/tours/all', {
    params: operatorId ? { operatorId } : undefined
  })
  return data
}

export type RecalculateTourTotalsResult = {
  operatorCount: number
  recalculatedTourCount: number
}

export async function recalculateTourTotals(operatorId?: string | null) {
  const { data } = await apiClient.post<RecalculateTourTotalsResult>('/api/admin/tours/recalculate-totals', null, {
    params: operatorId ? { operatorId } : undefined
  })
  return data
}

export async function getTourReadiness(operatorId?: string | null) {
  const { data } = await apiClient.get<TourReadiness[]>('/api/admin/tours/readiness', {
    params: operatorId ? { operatorId } : undefined
  })
  return data
}

export type UploadLogoResponse = {
  logoUrl: string
  objectKey: string
}

export async function uploadOperatorLogo(operatorId: string, file: File) {
  const formData = new FormData()
  formData.append('file', file)

  const { data } = await apiClient.post<UploadLogoResponse>(
    `/operators/${operatorId}/logo/upload`,
    formData
  )

  return data
}

export async function uploadTourHeroImage(tourId: string, file: File) {
  const formData = new FormData()
  formData.append('file', file)
  const { data } = await apiClient.post<TourSummary>(`/tours/${tourId}/hero-images/upload`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  })
  return data
}

export async function deleteTourHeroImage(tourId: string, imageUrl: string) {
  const { data } = await apiClient.delete<TourSummary>(`/tours/${tourId}/hero-images`, {
    data: imageUrl,
    headers: { 'Content-Type': 'application/json' }
  })
  return data
}

export type RegenerateThumbnailsResponse = {
  processed: number
  skipped: number
  failed: number
  errors: string[]
}

export async function regenerateTourThumbnails(tourId: string) {
  const { data } = await apiClient.post<RegenerateThumbnailsResponse>(
    `/tours/${tourId}/hero-images/regenerate-thumbnails`
  )
  return data
}

export async function regenerateAllTourThumbnails() {
  const { data } = await apiClient.post<RegenerateThumbnailsResponse>(
    `/tours/regenerate-all-thumbnails`
  )
  return data
}
